import { faro } from '@grafana/faro-web-sdk'
import { SCRATCH_BACKEND_BASE_URLS } from 'config'
import { captureException } from './sentry'

export type ErrorSource = 'axios' | 'react-query' | 'urql' | 'ag-grid' | 'manual'

export interface ErrorContext {
	source: ErrorSource
	url?: string
	operation?: string
	status?: number
}

const CONTROL_FLOW = ['setup-intent-pending', 'capture-cancelled']
const CANCELLED = ['ERR_CANCELED', 'AbortError', 'CanceledError']

const seen = new WeakSet<object>()

const getMessage = (error: unknown) => (error instanceof Error ? error.message : String(error))

export const shouldReport = (error: unknown, { status, url }: ErrorContext): boolean => {
	const { code, name } = (error ?? {}) as { code?: string; name?: string }
	const cancelled = CANCELLED.includes(code ?? '') || CANCELLED.includes(name ?? '')

	const thirdParty =
		Boolean(url?.startsWith('http')) &&
		SCRATCH_BACKEND_BASE_URLS.length > 0 &&
		!SCRATCH_BACKEND_BASE_URLS.some((baseURL) => url?.startsWith(baseURL))

	if (cancelled || thirdParty || CONTROL_FLOW.includes(getMessage(error))) {
		return false
	}

	return status !== 401
}

/** One failure, one event: whichever layer sees it first reports, the rest become no-ops. */
export const reportError = (error: unknown, context: ErrorContext): void => {
	if (typeof error === 'object' && error !== null) {
		if (seen.has(error)) {
			return
		}
		seen.add(error)
	}

	if (!shouldReport(error, context)) {
		return
	}

	const { source, url, operation, status } = context
	const asError = error instanceof Error ? error : new Error(getMessage(error))

	//report to Grafana
	faro.api?.pushError(asError, {
		type: source,
		context: {
			source,
			// Query strings carry secrets; Stripe client secrets reach our logs through urls today
			...(url ? { url: url.split('?')[0] } : {}),
			...(operation ? { operation } : {}),
			...(status === undefined ? {} : { status: String(status) })
		}
	})
	//report to Sentry
	captureException(asError)
}
